@file:Suppress("TooManyFunctions")

package top.wcpe.allincore.ui.web.model

import kotlin.js.JSON
import kotlin.js.jsTypeOf
import kotlin.js.unsafeCast
import top.wcpe.allincore.ui.web.asset.AssetBudget
import top.wcpe.allincore.ui.web.asset.AssetBudgetClass
import top.wcpe.allincore.ui.web.asset.AssetBudgetKey
import top.wcpe.allincore.ui.web.asset.AssetBudgetLimits
import top.wcpe.allincore.ui.web.asset.AssetPathPolicy
import top.wcpe.allincore.ui.web.asset.AssetPolicyException
import top.wcpe.allincore.ui.web.asset.AssetReservation

internal data class RestrictedBbInlineTextureDecode(
    val bytes: ByteArray,
    val budgetBytes: Long,
    val contentHash: String,
)

internal class RestrictedBbInlineTextureBudgetSeam(
    val estimateDecodedBytes: (String) -> Long = ::decodedBase64ByteCount,
    val contentHash: (String) -> String = ::inlineTextureContentHash,
    val decode: (String, String) -> RestrictedBbInlineTextureDecode = ::decodeInlineTexture,
)

internal object RestrictedBbModelParser {
    fun parse(
        text: String,
        bbmodelCanonicalPath: String,
        textureOverride: RestrictedBbTextureOverride? = null,
        assetBudget: AssetBudget = AssetBudget(),
        inlineTextureBudgetSeam: RestrictedBbInlineTextureBudgetSeam = RestrictedBbInlineTextureBudgetSeam(),
    ): RestrictedBbModel = ParserSession(
        text,
        bbmodelCanonicalPath,
        textureOverride,
        assetBudget,
        inlineTextureBudgetSeam,
    ).parse()
}

private class ParserSession(
    private val text: String,
    private val requestedModelPath: String,
    private val textureOverride: RestrictedBbTextureOverride?,
    private val assetBudget: AssetBudget,
    private val inlineTextureBudgetSeam: RestrictedBbInlineTextureBudgetSeam,
) {
    private val uuids = mutableSetOf<String>()
    private val warnings = mutableListOf<RestrictedBbWarning>()
    private val inlineReservations = mutableListOf<PendingInlineTextureReservation>()
    private var pendingInlineGenerationBytes = 0L
    private var outlinerNodes = 0

    fun parse(): RestrictedBbModel = try {
        val modelPath = canonicalModelPath(requestedModelPath)
        val root = parseJson(text).asObject("$")
        validateTopLevel(root)
        val resolution = parseResolution(root.required("resolution", "$"))
        val overrideRequest = textureOverride?.let(::overrideRequest)
        val textures = parseTextures(root.optionalArray("textures", "$").orEmpty(), modelPath, overrideRequest)
        val cubes = parseCubes(root.requiredArray("elements", "$"))
        validateFaceTextureReferences(cubes, textures)
        val roots = parseOutliner(root.optionalArray("outliner", "$"), cubes)
        buildModel(resolution, cubes, roots, textures).also { commitInlineReservations() }
    } catch (error: Throwable) {
        inlineReservations.forEach { it.reservation.cancel() }
        throw error
    }

    private fun retainInlineReservation(reservation: AssetReservation, actualBytes: Long, location: String) {
        val nextBytes = pendingInlineGenerationBytes + reservation.chargedBytes
        if (assetBudget.generationBytes + nextBytes > AssetBudgetLimits.GENERATION_BYTES) {
            reservation.cancel()
            failure(BBMODEL_LIMIT, location, "候选 generation 资产超过六十四 MiB")
        }
        pendingInlineGenerationBytes = nextBytes
        inlineReservations += PendingInlineTextureReservation(reservation, actualBytes)
    }

    private fun commitInlineReservations() {
        inlineReservations.forEach { pending -> pending.reservation.commit(pending.actualBytes) }
        inlineReservations.clear()
        pendingInlineGenerationBytes = 0
    }

    private fun buildModel(
        resolution: BbResolution,
        cubes: List<RestrictedBbCube>,
        roots: List<RestrictedBbOutlinerNode>,
        textures: List<RestrictedBbTexture>,
    ): RestrictedBbModel {
        val faceCount = cubes.sumOf { it.faces.size }
        val vertexCount = faceCount * VERTICES_PER_FACE
        val textureObjects = if (textureOverride != null && textures.isNotEmpty()) 1 else textures.size
        val materialObjects = conservativeMaterialCount(cubes, textureOverride != null)
        val gpuBudget = RestrictedBbGpuBudget(cubes.size, materialObjects, textureObjects)
        validateBudgets(cubes.size, faceCount, vertexCount, gpuBudget)
        return RestrictedBbModel(
            resolution,
            cubes.toList(),
            roots.toList(),
            textures.toList(),
            RestrictedBbStructureBudget(cubes.size, outlinerNodes, faceCount, vertexCount),
            gpuBudget,
            warnings.toList(),
        )
    }

    private fun validateTopLevel(root: JsonObject) {
        root.requireOnly(TOP_LEVEL_FIELDS, "$")
        root.requireScalarFields(TOP_SCALAR_METADATA, "$")
        rejectDrawableTopLevel(root)
        root.value("meta")?.asObject("$.meta")
        recordIgnoredMetadata(root, "animations", ANIMATION_WARNING, "模型动画已忽略")
        recordIgnoredMetadata(root, "locators", LOCATOR_WARNING, "模型 locator 元数据已忽略")
    }

    private fun rejectDrawableTopLevel(root: JsonObject) {
        DRAWABLE_TOP_LEVEL_FIELDS.firstOrNull(root::contains)?.let { field ->
            failure(BBMODEL_UNSUPPORTED, "$.$field", "首期不支持 $field drawable")
        }
        if (root.contains("display")) {
            failure(BBMODEL_UNSUPPORTED, "$.display", "首期不支持 display transform")
        }
    }

    private fun recordIgnoredMetadata(root: JsonObject, field: String, code: String, message: String) {
        val value = root.value(field) ?: return
        if (value !is JsonArray && value !is JsonObject) {
            failure(BBMODEL_SCHEMA, "$.$field", "$field 必须是数组或对象")
        }
        warnings += RestrictedBbWarning(code, "$.$field", message)
    }

    private fun parseResolution(value: JsonValue): BbResolution {
        val location = "$.resolution"
        val objectValue = value.asObject(location)
        objectValue.requireOnly(RESOLUTION_FIELDS, location)
        val width = objectValue.requiredFiniteNumber("width", location)
        val height = objectValue.requiredFiniteNumber("height", location)
        if (width <= 0.0 || height <= 0.0) failure(BBMODEL_SCHEMA, location, "resolution 必须大于零")
        return BbResolution(width, height)
    }

    private fun parseTextures(
        values: List<JsonValue>,
        modelPath: String,
        overrideRequest: RestrictedBbRelativeTextureRequest?,
    ): List<RestrictedBbTexture> {
        val indices = mutableSetOf<Int>()
        val names = mutableSetOf<String>()
        return values.mapIndexed { position, value ->
            parseTexture(value, position, modelPath, overrideRequest, indices, names)
        }
    }

    private fun parseTexture(
        value: JsonValue,
        position: Int,
        modelPath: String,
        overrideRequest: RestrictedBbRelativeTextureRequest?,
        indices: MutableSet<Int>,
        names: MutableSet<String>,
    ): RestrictedBbTexture {
        val location = "$.textures[$position]"
        val objectValue = value.asObject(location)
        objectValue.requireOnly(TEXTURE_FIELDS, location)
        objectValue.requireScalarFields(TEXTURE_SCALAR_METADATA, location)
        val index = parseTextureDeclarationIndex(objectValue.value("id"), position, "$location.id")
        if (!indices.add(index)) failure(BBMODEL_REFERENCE, "$location.id", "纹理索引重复：$index")
        val name = objectValue.optionalString("name", location)?.also { textureName ->
            if (textureName.isEmpty()) failure(BBMODEL_SCHEMA, "$location.name", "纹理名称不能为空")
            if (!names.add(textureName)) failure(BBMODEL_REFERENCE, "$location.name", "纹理名称重复：$textureName")
        }
        objectValue.optionalString("uuid", location)?.let { registerUuid(it, "$location.uuid") }
        val selection = selectTextureSource(objectValue, location, modelPath, position, overrideRequest)
        return RestrictedBbTexture(index, name, selection.first, selection.second)
    }

    private fun selectTextureSource(
        value: JsonObject,
        location: String,
        modelPath: String,
        inlineIndex: Int,
        overrideRequest: RestrictedBbRelativeTextureRequest?,
    ): Pair<RestrictedBbTextureSourceKind, RestrictedBbTextureSource> {
        if (overrideRequest != null) {
            return RestrictedBbTextureSourceKind.DSL_OVERRIDE to RestrictedBbExternalTexture(overrideRequest)
        }
        if (value.contains("relative_path")) {
            val relative = value.requiredString("relative_path", location)
            if (relative.isEmpty()) failure(BBMODEL_TEXTURE, "$location.relative_path", "relative_path 不能为空")
            val request = fileRelativeRequest(modelPath, relative, "$location.relative_path")
            return RestrictedBbTextureSourceKind.RELATIVE_PATH to RestrictedBbExternalTexture(request)
        }
        if (value.contains("source")) {
            val inline = parseInlineTexture(
                value.requiredString("source", location),
                "$location.source",
                modelPath,
                inlineIndex,
            )
            return RestrictedBbTextureSourceKind.INLINE_DATA to inline
        }
        failure(BBMODEL_TEXTURE, location, "纹理必须声明 relative_path 或 source")
    }

    private fun parseInlineTexture(
        source: String,
        location: String,
        modelPath: String,
        inlineIndex: Int,
    ): RestrictedBbInlineTexture {
        val match = INLINE_TEXTURE.matchEntire(source)
            ?: failure(BBMODEL_TEXTURE, location, "内嵌纹理必须是严格 data:image/png|jpeg|webp;base64")
        val mime = "image/${match.groupValues[1]}"
        val encoded = match.groupValues[2]
        val estimatedKey = AssetBudgetKey.InlineTexture(
            modelPath,
            inlineIndex,
            inlineTextureBudgetSeam.contentHash(encoded),
        )
        val decoded = decodeReservedInlineTexture(
            assetBudget,
            estimatedKey,
            encoded,
            mime,
            location,
            inlineTextureBudgetSeam,
        )
        val actualKey = AssetBudgetKey.InlineTexture(modelPath, inlineIndex, decoded.contentHash)
        val actual = reserveInlineTexture(assetBudget, actualKey, decoded.budgetBytes, location)
        try {
            retainInlineReservation(actual, decoded.budgetBytes, location)
            return RestrictedBbInlineTexture(mime, decoded.bytes)
        } catch (error: Throwable) {
            actual.cancel()
            throw error
        }
    }

    private fun parseCubes(values: List<JsonValue>): List<RestrictedBbCube> {
        if (values.size > RestrictedBbModelLimits.CUBES) {
            failure(BBMODEL_LIMIT, "$.elements", "cube 数超过 ${RestrictedBbModelLimits.CUBES}")
        }
        return values.mapIndexed(::parseCube)
    }

    private fun parseCube(index: Int, value: JsonValue): RestrictedBbCube {
        val location = "$.elements[$index]"
        val objectValue = value.asObject(location)
        objectValue.requireOnly(CUBE_FIELDS, location)
        objectValue.requireScalarFields(COMMON_SCALAR_METADATA, location)
        val type = objectValue.optionalString("type", location) ?: "cube"
        if (type != "cube") failure(BBMODEL_UNSUPPORTED, "$location.type", "首期仅支持 cube，拒绝 $type")
        val uuid = objectValue.requiredString("uuid", location)
        registerUuid(uuid, "$location.uuid")
        return RestrictedBbCube(
            uuid,
            objectValue.optionalString("name", location),
            parseVector3(objectValue.required("from", location), "$location.from"),
            parseVector3(objectValue.required("to", location), "$location.to"),
            parseOptionalVector3(objectValue.value("origin"), "$location.origin"),
            parseOptionalVector3(objectValue.value("rotation"), "$location.rotation"),
            parseFaces(objectValue.required("faces", location), "$location.faces"),
        )
    }

    private fun parseFaces(value: JsonValue, location: String): List<RestrictedBbFace> {
        val objectValue = value.asObject(location)
        objectValue.requireOnly(FACE_NAMES, location)
        val missing = FACE_DIRECTIONS.firstOrNull { !objectValue.contains(it.first) }
        if (missing != null) failure(BBMODEL_SCHEMA, location, "cube 必须声明六个 face，缺少 ${missing.first}")
        return FACE_DIRECTIONS.map { (name, direction) ->
            parseFace(objectValue.required(name, location), "$location.$name", direction)
        }
    }

    private fun parseFace(value: JsonValue, location: String, direction: BbFaceDirection): RestrictedBbFace {
        val objectValue = value.asObject(location)
        objectValue.requireOnly(FACE_FIELDS, location)
        objectValue.requireScalarFields(FACE_SCALAR_METADATA, location)
        val uv = parseUv(objectValue.required("uv", location), "$location.uv")
        val texture = parseTextureReference(objectValue.required("texture", location), "$location.texture")
        return RestrictedBbFace(direction, uv, texture)
    }

    private fun validateFaceTextureReferences(
        cubes: List<RestrictedBbCube>,
        textures: List<RestrictedBbTexture>,
    ) {
        val indices = textures.mapTo(mutableSetOf()) { it.index }
        cubes.forEachIndexed { cubeIndex, cube ->
            cube.faces.forEach { face ->
                if (face.textureIndex !in indices) {
                    val faceName = face.direction.name.lowercase()
                    failure(
                        BBMODEL_REFERENCE,
                        "$.elements[$cubeIndex].faces.$faceName.texture",
                        "纹理索引不存在：${face.textureIndex}",
                    )
                }
            }
        }
    }

    private fun parseOutliner(
        values: List<JsonValue>?,
        cubes: List<RestrictedBbCube>,
    ): List<RestrictedBbOutlinerNode> {
        if (values == null) return cubes.map { RestrictedBbCubeNode(it.uuid) }
        val cubesByUuid = cubes.associateBy { it.uuid }
        val parents = mutableSetOf<String>()
        val activeGroups = mutableListOf<String>()
        val roots = values.mapIndexed { index, value ->
            parseOutlinerNode(value, "$.outliner[$index]", 1, cubesByUuid, parents, activeGroups)
        }.toMutableList()
        cubes.filterNot { it.uuid in parents }.forEach { roots += RestrictedBbCubeNode(it.uuid) }
        return roots
    }

    private fun parseOutlinerNode(
        value: JsonValue,
        location: String,
        depth: Int,
        cubes: Map<String, RestrictedBbCube>,
        parents: MutableSet<String>,
        activeGroups: MutableList<String>,
    ): RestrictedBbOutlinerNode {
        countOutlinerNode(location)
        return when (value) {
            is JsonString -> parseCubeReference(value.value, location, cubes, parents, activeGroups)
            is JsonObject -> parseGroup(value, location, depth, cubes, parents, activeGroups)
            else -> failure(BBMODEL_SCHEMA, location, "outliner 节点必须是 group 对象或 cube uuid")
        }
    }

    private fun parseCubeReference(
        uuid: String,
        location: String,
        cubes: Map<String, RestrictedBbCube>,
        parents: MutableSet<String>,
        activeGroups: List<String>,
    ): RestrictedBbCubeNode {
        if (uuid in activeGroups) {
            val start = activeGroups.indexOf(uuid)
            val cycle = (activeGroups.drop(start) + uuid).joinToString(" -> ")
            failure(BBMODEL_REFERENCE, location, "outliner 循环：$cycle")
        }
        if (uuid !in cubes) failure(BBMODEL_REFERENCE, location, "cube 引用不存在：$uuid")
        if (!parents.add(uuid)) failure(BBMODEL_REFERENCE, location, "cube 被多个父节点引用：$uuid")
        return RestrictedBbCubeNode(uuid)
    }

    private fun parseGroup(
        value: JsonObject,
        location: String,
        depth: Int,
        cubes: Map<String, RestrictedBbCube>,
        parents: MutableSet<String>,
        activeGroups: MutableList<String>,
    ): RestrictedBbGroupNode {
        if (depth > RestrictedBbModelLimits.OUTLINER_DEPTH) {
            failure(BBMODEL_LIMIT, location, "outliner 层级深度超过 ${RestrictedBbModelLimits.OUTLINER_DEPTH}")
        }
        value.requireOnly(GROUP_FIELDS, location)
        value.requireScalarFields(COMMON_SCALAR_METADATA, location)
        val type = value.optionalString("type", location)
        if (type != null && type !in GROUP_TYPES) {
            failure(BBMODEL_UNSUPPORTED, "$location.type", "首期仅支持 group 或 bone，拒绝 $type")
        }
        val uuid = value.requiredString("uuid", location)
        if (uuid in activeGroups) {
            val start = activeGroups.indexOf(uuid)
            val cycle = (activeGroups.drop(start) + uuid).joinToString(" -> ")
            failure(BBMODEL_REFERENCE, "$location.uuid", "outliner 循环：$cycle")
        }
        registerUuid(uuid, "$location.uuid")
        activeGroups += uuid
        val children = parseGroupChildren(value, location, depth, cubes, parents, activeGroups)
        if (activeGroups.removeLast() != uuid) {
            failure(BBMODEL_REFERENCE, location, "outliner 层级状态非法")
        }
        return RestrictedBbGroupNode(
            uuid,
            value.optionalString("name", location),
            parseOptionalVector3(value.value("origin"), "$location.origin"),
            parseOptionalVector3(value.value("rotation"), "$location.rotation"),
            children,
        )
    }

    private fun parseGroupChildren(
        value: JsonObject,
        location: String,
        depth: Int,
        cubes: Map<String, RestrictedBbCube>,
        parents: MutableSet<String>,
        activeGroups: MutableList<String>,
    ): List<RestrictedBbOutlinerNode> = value.requiredArray("children", location).mapIndexed { index, child ->
        parseOutlinerNode(child, "$location.children[$index]", depth + 1, cubes, parents, activeGroups)
    }

    private fun countOutlinerNode(location: String) {
        outlinerNodes += 1
        if (outlinerNodes > RestrictedBbModelLimits.OUTLINER_NODES) {
            failure(BBMODEL_LIMIT, location, "outliner 节点数超过 ${RestrictedBbModelLimits.OUTLINER_NODES}")
        }
    }

    private fun registerUuid(uuid: String, location: String) {
        if (uuid.isEmpty()) failure(BBMODEL_SCHEMA, location, "uuid 不能为空")
        if (!uuids.add(uuid)) failure(BBMODEL_REFERENCE, location, "uuid 重复：$uuid")
    }
}

private data class PendingInlineTextureReservation(
    val reservation: AssetReservation,
    val actualBytes: Long,
)

private fun parseJson(text: String): JsonValue {
    val native = try {
        JSON.parse<dynamic>(text)
    } catch (error: Throwable) {
        failure(BBMODEL_JSON, "$", "模型 JSON 语法非法")
    }
    return normalizeJson(native, 1, "$")
}

private fun normalizeJson(value: dynamic, depth: Int, location: String): JsonValue {
    if (value == null) return JsonNull
    return when {
        isJsArray(value) -> {
            requireJsonDepth(depth, location)
            normalizeArray(value, depth, location)
        }
        jsTypeOf(value) == "object" -> {
            requireJsonDepth(depth, location)
            normalizeObject(value, depth, location)
        }
        jsTypeOf(value) == "string" -> JsonString(value.unsafeCast<String>())
        jsTypeOf(value) == "boolean" -> JsonBoolean
        jsTypeOf(value) == "number" -> normalizeNumber(value.unsafeCast<Double>(), location)
        else -> failure(BBMODEL_JSON, location, "模型 JSON 包含非法值")
    }
}

private fun requireJsonDepth(depth: Int, location: String) {
    if (depth > RestrictedBbModelLimits.JSON_DEPTH) {
        failure(BBMODEL_LIMIT, location, "JSON 嵌套深度超过 ${RestrictedBbModelLimits.JSON_DEPTH}")
    }
}

private fun normalizeArray(value: dynamic, depth: Int, location: String): JsonArray {
    val array = value.unsafeCast<Array<dynamic>>()
    return JsonArray(array.mapIndexed { index, child -> normalizeJson(child, depth + 1, "$location[$index]") })
}

private fun normalizeObject(value: dynamic, depth: Int, location: String): JsonObject {
    val entries = linkedMapOf<String, JsonValue>()
    objectKeys(value).forEach { key -> entries[key] = normalizeJson(value[key], depth + 1, "$location.$key") }
    return JsonObject(entries)
}

private fun normalizeNumber(value: Double, location: String): JsonNumber {
    if (!value.isFinite()) failure(BBMODEL_SCHEMA, location, "数值必须是有限数值")
    return JsonNumber(value)
}

private fun parseVector3(value: JsonValue, location: String): BbVector3 {
    val values = value.asArray(location)
    if (values.size != VECTOR_SIZE) failure(BBMODEL_SCHEMA, location, "数组长度必须为 3")
    return BbVector3(
        values[0].asFiniteNumber("$location[0]"),
        values[1].asFiniteNumber("$location[1]"),
        values[2].asFiniteNumber("$location[2]"),
    )
}

private fun parseOptionalVector3(value: JsonValue?, location: String): BbVector3 =
    if (value == null) ZERO_VECTOR else parseVector3(value, location)

private fun parseUv(value: JsonValue, location: String): BbUvRect {
    val values = value.asArray(location)
    if (values.size != UV_SIZE) failure(BBMODEL_SCHEMA, location, "UV 数组长度必须为 4")
    return BbUvRect(
        values[0].asFiniteNumber("$location[0]"),
        values[1].asFiniteNumber("$location[1]"),
        values[2].asFiniteNumber("$location[2]"),
        values[3].asFiniteNumber("$location[3]"),
    )
}

private fun parseTextureReference(value: JsonValue, location: String): Int = when (value) {
    is JsonNumber -> value.value.toNonNegativeInt(location, "纹理索引")
    is JsonString -> value.value.removePrefix("#").toIntOrNull()
        ?.takeIf { it >= 0 }
        ?: failure(BBMODEL_SCHEMA, location, "纹理索引必须是非负整数")
    else -> failure(BBMODEL_SCHEMA, location, "纹理索引必须是非负整数")
}

private fun parseTextureDeclarationIndex(value: JsonValue?, fallback: Int, location: String): Int = when (value) {
    null -> fallback
    is JsonNumber -> value.value.toNonNegativeInt(location, "纹理索引")
    is JsonString -> value.value.toIntOrNull()
        ?.takeIf { it >= 0 }
        ?: failure(BBMODEL_SCHEMA, location, "纹理 id 必须是非负整数")
    else -> failure(BBMODEL_SCHEMA, location, "纹理 id 必须是非负整数")
}

private fun Double.toNonNegativeInt(location: String, name: String): Int {
    if (!isFinite() || this < 0.0 || this % 1.0 != 0.0 || this > Int.MAX_VALUE.toDouble()) {
        failure(BBMODEL_SCHEMA, location, "$name 必须是非负整数")
    }
    return toInt()
}

private fun overrideRequest(override: RestrictedBbTextureOverride): RestrictedBbRelativeTextureRequest {
    if (override.relativePath.isEmpty()) failure(BBMODEL_TEXTURE, "$.textureOverride", "DSL texture 覆盖不能为空")
    val base = try {
        AssetPathPolicy.canonicalDirectory(override.canonicalBaseDirectory)
    } catch (error: Throwable) {
        failure(BBMODEL_TEXTURE, "$.textureOverride", error.message ?: "DSL texture base 非法")
    }
    val canonical = canonicalRelativePath(base, override.relativePath, "$.textureOverride")
    requireImagePath(canonical, "$.textureOverride")
    return RestrictedBbRelativeTextureRequest(
        base,
        override.relativePath,
        canonical,
        RestrictedBbRelativeBase.DIRECTORY,
    )
}

private fun fileRelativeRequest(
    modelPath: String,
    relativePath: String,
    location: String,
): RestrictedBbRelativeTextureRequest {
    val directory = modelPath.substringBeforeLast('/', "")
    val canonical = canonicalRelativePath(directory, relativePath, location)
    requireImagePath(canonical, location)
    return RestrictedBbRelativeTextureRequest(
        modelPath,
        relativePath,
        canonical,
        RestrictedBbRelativeBase.FILE,
    )
}

private fun canonicalRelativePath(base: String, relativePath: String, location: String): String = try {
    AssetPathPolicy.relative(base, relativePath.removePrefix("./"))
} catch (error: Throwable) {
    failure(BBMODEL_TEXTURE, location, error.message ?: "纹理相对路径非法")
}

private fun canonicalModelPath(path: String): String {
    val canonical = try {
        AssetPathPolicy.external(path)
    } catch (error: Throwable) {
        failure(BBMODEL_SCHEMA, "$.modelPath", error.message ?: "模型 canonical path 非法")
    }
    if (!canonical.lowercase().endsWith(".bbmodel")) {
        failure(BBMODEL_SCHEMA, "$.modelPath", "模型路径必须使用 .bbmodel 扩展名")
    }
    return canonical
}

private fun requireImagePath(path: String, location: String) {
    if (IMAGE_EXTENSIONS.none { path.lowercase().endsWith(it) }) {
        failure(BBMODEL_TEXTURE, location, "纹理路径必须是 PNG、JPEG 或 WebP")
    }
}

private fun decodeReservedInlineTexture(
    assetBudget: AssetBudget,
    key: AssetBudgetKey.InlineTexture,
    encoded: String,
    mime: String,
    location: String,
    budgetSeam: RestrictedBbInlineTextureBudgetSeam,
): RestrictedBbInlineTextureDecode {
    val reservation = reserveInlineTexture(assetBudget, key, budgetSeam.estimateDecodedBytes(encoded), location)
    return try {
        budgetSeam.decode(encoded, location).also { decoded ->
            if (!matchesImageMagic(mime, decoded.bytes)) {
                failure(BBMODEL_TEXTURE, location, "内嵌纹理 MIME 与图片 magic 冲突")
            }
        }
    } finally {
        reservation.cancel()
    }
}

private fun reserveInlineTexture(
    assetBudget: AssetBudget,
    key: AssetBudgetKey.InlineTexture,
    estimatedBytes: Long,
    location: String,
) = try {
    assetBudget.reserveKnown(key, AssetBudgetClass.IMAGE, estimatedBytes)
} catch (error: AssetPolicyException) {
    failure(BBMODEL_LIMIT, location, error.message ?: "内嵌纹理预算超限")
}

private fun decodeInlineTexture(encoded: String, location: String): RestrictedBbInlineTextureDecode {
    val bytes = decodeStrictBase64(encoded, location)
    return RestrictedBbInlineTextureDecode(bytes, bytes.size.toLong(), inlineTextureContentHash(encoded))
}

private fun decodedBase64ByteCount(encoded: String): Long {
    val padding = when {
        encoded.endsWith("==") -> 2
        encoded.endsWith('=') -> 1
        else -> 0
    }
    return encoded.length.toLong() / BASE64_BLOCK * 3 - padding
}

private fun inlineTextureContentHash(encoded: String): String {
    var hash = FNV_OFFSET_BASIS
    encoded.forEach { character ->
        hash = (hash xor character.code.toLong()) * FNV_PRIME
    }
    return hash.toString()
}

private fun decodeStrictBase64(encoded: String, location: String): ByteArray {
    if (encoded.isEmpty() || encoded.length % BASE64_BLOCK != 0 || !BASE64_PAYLOAD.matches(encoded)) {
        failure(BBMODEL_TEXTURE, location, "内嵌纹理 base64 不规范")
    }
    val decoded = try {
        atob(encoded)
    } catch (error: Throwable) {
        failure(BBMODEL_TEXTURE, location, "内嵌纹理 base64 解码失败")
    }
    if (btoa(decoded) != encoded) failure(BBMODEL_TEXTURE, location, "内嵌纹理 base64 不规范")
    return ByteArray(decoded.length) { index -> decoded[index].code.toByte() }
}

private fun matchesImageMagic(mime: String, bytes: ByteArray): Boolean = when (mime) {
    "image/png" -> bytes.matchesAt(0, 0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A)
    "image/jpeg" -> bytes.matchesAt(0, 0xFF, 0xD8, 0xFF)
    "image/webp" -> bytes.matchesAt(0, 0x52, 0x49, 0x46, 0x46) && bytes.matchesAt(8, 0x57, 0x45, 0x42, 0x50)
    else -> false
}

private fun ByteArray.matchesAt(offset: Int, vararg expected: Int): Boolean =
    size >= offset + expected.size && expected.indices.all { index ->
        (this[offset + index].toInt() and 0xFF) == expected[index]
    }

private fun conservativeMaterialCount(cubes: List<RestrictedBbCube>, hasOverride: Boolean): Int {
    val identities = mutableSetOf<Pair<Int, BbFaceDirection>>()
    cubes.forEach { cube ->
        cube.faces.forEach { face ->
            val textureIdentity = if (hasOverride) DSL_OVERRIDE_TEXTURE_ID else face.textureIndex
            identities += textureIdentity to face.direction
        }
    }
    return identities.size
}

private fun validateBudgets(
    cubeCount: Int,
    faceCount: Int,
    vertexCount: Int,
    gpu: RestrictedBbGpuBudget,
) {
    requireLimit(cubeCount, RestrictedBbModelLimits.CUBES, "cube")
    requireLimit(faceCount, RestrictedBbModelLimits.FACES, "face")
    requireLimit(vertexCount, RestrictedBbModelLimits.VERTICES, "vertex")
    requireLimit(gpu.geometryObjects, RestrictedBbModelLimits.GEOMETRIES, "geometry")
    requireLimit(gpu.materialObjects, RestrictedBbModelLimits.MATERIALS, "material")
    requireLimit(gpu.textureObjects, RestrictedBbModelLimits.TEXTURES, "texture")
}

private fun requireLimit(actual: Int, maximum: Int, name: String) {
    if (actual > maximum) failure(BBMODEL_LIMIT, "$.budget.$name", "$name 数超过 $maximum")
}

private fun JsonObject.requireOnly(allowed: Set<String>, location: String) {
    val unknown = fields.keys.firstOrNull { it !in allowed } ?: return
    failure(BBMODEL_SCHEMA, "$location.$unknown", "存在不支持的字段 $unknown")
}

private fun JsonObject.requireScalarFields(names: Set<String>, location: String) {
    names.forEach { name ->
        val value = fields[name] ?: return@forEach
        if (value is JsonObject || value is JsonArray) {
            failure(BBMODEL_SCHEMA, "$location.$name", "可忽略元数据必须是标量")
        }
    }
}

private fun JsonObject.required(name: String, location: String): JsonValue =
    fields[name] ?: failure(BBMODEL_SCHEMA, "$location.$name", "缺少必填字段 $name")

private fun JsonObject.requiredArray(name: String, location: String): List<JsonValue> =
    required(name, location).asArray("$location.$name")

private fun JsonObject.optionalArray(name: String, location: String): List<JsonValue>? =
    fields[name]?.asArray("$location.$name")

private fun JsonObject.requiredString(name: String, location: String): String =
    required(name, location).asString("$location.$name")

private fun JsonObject.optionalString(name: String, location: String): String? =
    fields[name]?.asString("$location.$name")

private fun JsonObject.requiredFiniteNumber(name: String, location: String): Double =
    required(name, location).asFiniteNumber("$location.$name")

private fun JsonObject.contains(name: String): Boolean = fields.containsKey(name)

private fun JsonObject.value(name: String): JsonValue? = fields[name]

private fun JsonValue.asObject(location: String): JsonObject =
    this as? JsonObject ?: failure(BBMODEL_SCHEMA, location, "字段必须是 JSON 对象")

private fun JsonValue.asArray(location: String): List<JsonValue> =
    (this as? JsonArray)?.values ?: failure(BBMODEL_SCHEMA, location, "字段必须是 JSON 数组")

private fun JsonValue.asString(location: String): String =
    (this as? JsonString)?.value ?: failure(BBMODEL_SCHEMA, location, "字段必须是字符串")

private fun JsonValue.asFiniteNumber(location: String): Double {
    val number = (this as? JsonNumber)?.value
        ?: failure(BBMODEL_SCHEMA, location, "字段必须是有限数值")
    if (!number.isFinite()) failure(BBMODEL_SCHEMA, location, "字段必须是有限数值")
    return number
}

private sealed interface JsonValue

private data class JsonObject(val fields: Map<String, JsonValue>) : JsonValue

private data class JsonArray(val values: List<JsonValue>) : JsonValue

private data class JsonString(val value: String) : JsonValue

private data class JsonNumber(val value: Double) : JsonValue

private data object JsonBoolean : JsonValue

private data object JsonNull : JsonValue

private fun failure(code: String, location: String, detail: String): Nothing =
    throw RestrictedBbModelException(code, location, detail)

private fun isJsArray(value: dynamic): Boolean = js("Array.isArray(value)").unsafeCast<Boolean>()

private fun objectKeys(value: dynamic): Array<String> = js("Object.keys(value)").unsafeCast<Array<String>>()

private external fun atob(encodedData: String): String

private external fun btoa(data: String): String

private const val VECTOR_SIZE = 3
private const val UV_SIZE = 4
private const val VERTICES_PER_FACE = 4
private const val BASE64_BLOCK = 4
private const val DSL_OVERRIDE_TEXTURE_ID = -1
private const val FNV_OFFSET_BASIS = -3750763034362895579L
private const val FNV_PRIME = 1099511628211L
private val ZERO_VECTOR = BbVector3(0.0, 0.0, 0.0)
private val FACE_DIRECTIONS = listOf(
    "north" to BbFaceDirection.NORTH,
    "east" to BbFaceDirection.EAST,
    "south" to BbFaceDirection.SOUTH,
    "west" to BbFaceDirection.WEST,
    "up" to BbFaceDirection.UP,
    "down" to BbFaceDirection.DOWN,
)
private val FACE_NAMES = FACE_DIRECTIONS.mapTo(mutableSetOf()) { it.first }
private val TOP_LEVEL_FIELDS = setOf(
    "resolution",
    "elements",
    "outliner",
    "textures",
    "meta",
    "animations",
    "locators",
    "name",
    "color",
    "visibility",
    "locked",
    "uuid",
    "animation_variable_placeholders",
    "variable_placeholders",
    "display",
    "mesh",
    "meshes",
)
private val DRAWABLE_TOP_LEVEL_FIELDS = setOf("mesh", "meshes")
private val TOP_SCALAR_METADATA = setOf(
    "name",
    "color",
    "visibility",
    "locked",
    "uuid",
    "animation_variable_placeholders",
    "variable_placeholders",
)
private val COMMON_SCALAR_METADATA = setOf("color", "visibility", "locked")
private val FACE_SCALAR_METADATA = COMMON_SCALAR_METADATA + setOf("name", "uuid")
private val TEXTURE_SCALAR_METADATA = COMMON_SCALAR_METADATA + setOf("folder", "namespace")
private val RESOLUTION_FIELDS = setOf("width", "height")
private val CUBE_FIELDS = setOf(
    "uuid",
    "type",
    "from",
    "to",
    "origin",
    "rotation",
    "faces",
    "name",
    "color",
    "visibility",
    "locked",
)
private val FACE_FIELDS = setOf("uv", "texture", "name", "color", "visibility", "locked", "uuid")
private val GROUP_FIELDS = setOf(
    "uuid",
    "type",
    "name",
    "origin",
    "rotation",
    "children",
    "color",
    "visibility",
    "locked",
)
private val GROUP_TYPES = setOf("group", "bone")
private val TEXTURE_FIELDS = setOf(
    "id",
    "name",
    "uuid",
    "relative_path",
    "source",
    "folder",
    "namespace",
    "color",
    "visibility",
    "locked",
)
private val IMAGE_EXTENSIONS = setOf(".png", ".jpg", ".jpeg", ".webp")
private val INLINE_TEXTURE = Regex("^data:image/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$")
private val BASE64_PAYLOAD = Regex("^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$")

private const val BBMODEL_JSON = "BBMODEL_JSON_INVALID"
private const val BBMODEL_SCHEMA = "BBMODEL_SCHEMA_INVALID"
private const val BBMODEL_REFERENCE = "BBMODEL_REFERENCE_INVALID"
private const val BBMODEL_TEXTURE = "BBMODEL_TEXTURE_INVALID"
private const val BBMODEL_UNSUPPORTED = "BBMODEL_UNSUPPORTED"
private const val BBMODEL_LIMIT = "BBMODEL_LIMIT_EXCEEDED"
private const val ANIMATION_WARNING = "BBMODEL_ANIMATION_IGNORED"
private const val LOCATOR_WARNING = "BBMODEL_LOCATOR_IGNORED"
